import React from 'react';
import Logo from './Logo';

const Hero: React.FC = () => {
  return (
    <section className="relative bg-gradient-to-br from-slate-950 via-slate-900 to-blue-950 text-white py-24 lg:py-36 overflow-hidden">
      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            'linear-gradient(rgba(99,179,237,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(99,179,237,0.3) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      {/* Glow accents */}
      <div className="absolute top-10 left-1/4 w-96 h-96 bg-blue-600 rounded-full filter blur-3xl opacity-15 pointer-events-none" />
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-indigo-600 rounded-full filter blur-3xl opacity-15 pointer-events-none" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl">
          {/* Logo lockup */}
          <div className="mb-6">
            <Logo size="lg" textColor="white" />
          </div>

          {/* Eyebrows */}
          <div className="flex flex-wrap items-center gap-2 mb-6">
            <span className="px-3.5 py-1 rounded-full bg-blue-500/10 border border-blue-400/30 text-blue-400 text-xs font-semibold uppercase tracking-widest">
              Custom Software · AI Automation · Cloud & FinOps
            </span>
            <span className="px-3.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-emerald-400 text-xs font-semibold uppercase tracking-widest flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-ping" />
              Creator of GNL Interpreters SaaS
            </span>
          </div>

          {/* Strong Headline (Recommendation #1) */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6 tracking-tight text-white">
            We Automate the Work Slowing Your Business Down.{' '}
            <span className="text-blue-400">Custom Software, AI & Cloud Solutions.</span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-300 mb-10 leading-relaxed max-w-3xl">
            Stop losing revenue to manual spreadsheets and repetitive tasks. We engineer enterprise-grade custom web apps, AI automations, and cloud platforms with 100% source code ownership.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <a
              href="#contact"
              className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white font-bold text-base px-8 py-4 rounded-xl transition-all duration-200 shadow-xl shadow-blue-900/40 hover:shadow-blue-600/50 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Book Free Automation Audit
            </a>
            <a
              href="#products"
              className="inline-flex items-center justify-center border border-slate-600 hover:border-blue-400 text-slate-200 hover:text-white font-semibold text-base px-8 py-4 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              Explore GNL & SaaS Products
            </a>
          </div>
        </div>

        {/* Quantifiable Stats Row (Recommendation #3) */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {[
            { value: '70%+', label: 'Manual Work Automated', sub: 'For client operational flows' },
            { value: 'Up to 40%', label: 'Cloud Bill Savings', sub: 'Via FinOps right-sizing' },
            { value: '200+', label: 'Languages Supported', sub: 'On GNL Interpreters SaaS' },
            { value: '100%', label: 'Source Code Ownership', sub: 'Zero lock-in, zero black boxes' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-slate-900/70 border border-slate-800 rounded-2xl px-6 py-5 text-left backdrop-blur-md hover:bg-slate-800/80 transition-colors"
            >
              <div className="text-2xl sm:text-3xl font-extrabold text-blue-400 font-mono mb-1">{stat.value}</div>
              <div className="text-sm font-bold text-white mb-0.5">{stat.label}</div>
              <div className="text-xs text-slate-400 font-medium">{stat.sub}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
